import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEventTiming } from './EventTimingContext';
import { SortableTimingRow } from './SortableTimingRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { 
  FIGURES_WARMUP_BASE_OPTIONS, 
  FIGURES_WARMUP_ADD_OPTIONS, 
  FIGURES_TIME_PER_FIGURE_OPTIONS 
} from './constants';

export default function FiguresTab() {
  const {
    isSuperUser,
    schedules,
    dialogOpen,
    setDialogOpen,
    editingTiming,
    setEditingTiming,
    saving,
    setSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    deleting,
    handleDeleteClick,
    handleConfirmDelete,
    selectedScheduleId,
    setSelectedScheduleId,
    timingsLists,
    fetchTimings,
    sensors,
    handleDragEnd,
    canEditTiming,
    resetForm,
    formatTimeOnBlur,
    shouldShowHR,
  } = useEventTiming();

  const [figuresGrades, setFiguresGrades] = useState([]);
  const [loopsGrades, setLoopsGrades] = useState([]);
  const [figuresTimings, setFiguresTimings] = useState({});
  const [loopsTimings, setLoopsTimings] = useState({});
  const [loopActionChanges, setLoopActionChanges] = useState('6:00');
  const [midEventWarmups, setMidEventWarmups] = useState('2:00');

  const timingsList = timingsLists.figures || [];

  // Fetch Figures and Loops grades when schedule is selected
  useEffect(() => {
    if (!selectedScheduleId || editingTiming) return;

    const fetchFiguresAndLoopsGrades = async () => {
      try {
        const [figRes, loopsRes] = await Promise.all([
          axios.get(`${API}/event-timings/figures-grades/${selectedScheduleId}`, { withCredentials: true }),
          axios.get(`${API}/event-timings/loops-grades/${selectedScheduleId}`, { withCredentials: true })
        ]);
        
        const figGrades = figRes.data || [];
        const loopGrades = loopsRes.data || [];
        
        setFiguresGrades(figGrades);
        setLoopsGrades(loopGrades);
        
        const figTimings = {};
        figGrades.forEach(g => {
          figTimings[g.grade_id] = {
            warmup_regionals: '5:00',
            warmup_regionals_add: '2:00',
            warmup_area_state: '7:00',
            warmup_area_state_add: '2:00',
            warmup_nationals: '10:00',
            warmup_nationals_add: '2:00',
            warmup_international: '10:00',
            warmup_international_add: '2:00',
            time_per_figure: '2:30',
            can_shorten: false
          };
        });
        setFiguresTimings(figTimings);
        
        const loopTimings = {};
        loopGrades.forEach(g => {
          loopTimings[g.grade_id] = {
            warmup_regionals: '5:00',
            warmup_regionals_add: '2:00',
            warmup_area_state: '5:00',
            warmup_area_state_add: '2:00',
            warmup_nationals: '5:00',
            warmup_nationals_add: '2:00',
            warmup_international: '5:00',
            warmup_international_add: '2:00',
            time_per_figure: '2:00',
            can_shorten: false
          };
        });
        setLoopsTimings(loopTimings);
        
        setLoopActionChanges('6:00');
        setMidEventWarmups('2:00');
      } catch (error) {
        console.error('Error fetching figures/loops grades:', error);
        toast.error('Failed to load grades for this schedule');
      }
    };
    fetchFiguresAndLoopsGrades();
  }, [selectedScheduleId, editingTiming]);

  const localResetForm = () => {
    resetForm();
    setFiguresGrades([]);
    setLoopsGrades([]);
    setFiguresTimings({});
    setLoopsTimings({});
    setLoopActionChanges('6:00');
    setMidEventWarmups('2:00');
  };

  const handleAddTimings = () => {
    localResetForm();
    setDialogOpen(true);
  };

  const handleEditTiming = async (timing) => {
    setEditingTiming(timing);
    setSelectedScheduleId(timing.schedule_id);
    
    try {
      const [figRes, loopsRes] = await Promise.all([
        axios.get(`${API}/event-timings/figures-grades/${timing.schedule_id}`, { withCredentials: true }),
        axios.get(`${API}/event-timings/loops-grades/${timing.schedule_id}`, { withCredentials: true })
      ]);
      
      const figGrades = figRes.data || [];
      const loopGrades = loopsRes.data || [];
      
      setFiguresGrades(figGrades);
      setLoopsGrades(loopGrades);
      
      const savedTimingsMap = {};
      (timing.grade_timings || []).forEach(gt => {
        savedTimingsMap[gt.grade_id] = gt;
      });
      
      const figTimings = {};
      figGrades.forEach(g => {
        const saved = savedTimingsMap[g.grade_id];
        figTimings[g.grade_id] = {
          warmup_regionals: saved?.warmup_regionals || '5:00',
          warmup_regionals_add: saved?.warmup_regionals_add || '2:00',
          warmup_area_state: saved?.warmup_area_state || '7:00',
          warmup_area_state_add: saved?.warmup_area_state_add || '2:00',
          warmup_nationals: saved?.warmup_nationals || '10:00',
          warmup_nationals_add: saved?.warmup_nationals_add || '2:00',
          warmup_international: saved?.warmup_international || '10:00',
          warmup_international_add: saved?.warmup_international_add || '2:00',
          time_per_figure: saved?.time_per_figure || '2:30',
          can_shorten: saved?.can_shorten || false
        };
      });
      setFiguresTimings(figTimings);
      
      const loopTimings = {};
      loopGrades.forEach(g => {
        const saved = savedTimingsMap[g.grade_id];
        loopTimings[g.grade_id] = {
          warmup_regionals: saved?.warmup_regionals || '5:00',
          warmup_regionals_add: saved?.warmup_regionals_add || '2:00',
          warmup_area_state: saved?.warmup_area_state || '5:00',
          warmup_area_state_add: saved?.warmup_area_state_add || '2:00',
          warmup_nationals: saved?.warmup_nationals || '5:00',
          warmup_nationals_add: saved?.warmup_nationals_add || '2:00',
          warmup_international: saved?.warmup_international || '5:00',
          warmup_international_add: saved?.warmup_international_add || '2:00',
          time_per_figure: saved?.time_per_figure || '2:00',
          can_shorten: saved?.can_shorten || false
        };
      });
      setLoopsTimings(loopTimings);
      
      setLoopActionChanges(timing.loop_action_changes || '6:00');
      setMidEventWarmups(timing.mid_event_warmups || '2:00');
    } catch (error) {
      console.error('Error fetching figures/loops grades for edit:', error);
      toast.error('Failed to load timing data');
      return;
    }
    
    setDialogOpen(true);
  };

  const updateGradeTiming = (gradeId, field, value, isLoops = false) => {
    if (isLoops) {
      setLoopsTimings(prev => ({
        ...prev,
        [gradeId]: { ...prev[gradeId], [field]: value }
      }));
    } else {
      setFiguresTimings(prev => ({
        ...prev,
        [gradeId]: { ...prev[gradeId], [field]: value }
      }));
    }
  };

  const handleSave = async () => {
    if (!selectedScheduleId) {
      toast.error('Please select a schedule');
      return;
    }

    setSaving(true);
    try {
      const allGradeTimings = [
        ...Object.entries(figuresTimings).map(([gradeId, timing]) => ({
          grade_id: gradeId,
          discipline: 'Figures',
          ...timing
        })),
        ...Object.entries(loopsTimings).map(([gradeId, timing]) => ({
          grade_id: gradeId,
          discipline: 'Loops',
          ...timing
        }))
      ];

      const payload = {
        schedule_id: selectedScheduleId,
        event_type: 'figures',
        grade_timings: allGradeTimings,
        loop_action_changes: loopActionChanges,
        mid_event_warmups: midEventWarmups
      };

      await axios.post(`${API}/event-timings`, payload, { withCredentials: true });
      
      toast.success(editingTiming ? 'Figures & Loops timings updated successfully' : 'Figures & Loops timings saved successfully');
      setDialogOpen(false);
      localResetForm();
      fetchTimings('figures');
    } catch (error) {
      console.error('Error saving timings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save timings');
    } finally {
      setSaving(false);
    }
  };

  const renderWarmupCell = (gradeId, baseField, addField, defaultBase, defaultAdd, isLoops) => {
    const timingsObj = isLoops ? loopsTimings : figuresTimings;
    const timing = timingsObj[gradeId] || {};
    const addValue = timing[addField] || defaultAdd;
    
    return (
      <div className="flex items-center gap-1">
        <Select 
          value={timing[baseField] || defaultBase} 
          onValueChange={(v) => updateGradeTiming(gradeId, baseField, v, isLoops)}
        >
          <SelectTrigger className="h-8 text-sm" style={{ width: '80px' }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
            {FIGURES_WARMUP_BASE_OPTIONS.map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">+</span>
        <Select 
          value={addValue} 
          onValueChange={(v) => updateGradeTiming(gradeId, addField, v, isLoops)}
        >
          <SelectTrigger className="h-8 text-sm" style={{ width: '80px' }}>
            <SelectValue>{addValue === 'none' ? '-' : addValue}</SelectValue>
          </SelectTrigger>
          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
            {FIGURES_WARMUP_ADD_OPTIONS.map(opt => (
              <SelectItem key={opt} value={opt}>{opt === 'none' ? '-' : opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const renderFiguresTable = (gradesList, timingsObj, isLoops = false) => {
    const defaultTimePerFigure = isLoops ? '2:00' : '2:30';
    
    return (
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-green-100">
            <th className="border px-3 py-2 text-left" style={{ width: '180px' }}>Grade</th>
            <th className="border px-3 py-2 text-center" style={{ width: '160px' }}>Warm Up Regionals</th>
            <th className="border px-3 py-2 text-center" style={{ width: '160px' }}>Warm Up Area/State</th>
            <th className="border px-3 py-2 text-center" style={{ width: '160px' }}>Warm Up Nationals</th>
            <th className="border px-3 py-2 text-center" style={{ width: '160px' }}>Warm Up International</th>
            <th className="border px-3 py-2 text-center" style={{ width: '100px' }}>Time per Figure</th>
            <th className="border px-3 py-2 text-center" style={{ width: '80px' }}>Can Shorten</th>
          </tr>
        </thead>
        <tbody>
          {gradesList.map((grade, index) => {
            const timing = timingsObj[grade.grade_id] || {};
            const showHR = shouldShowHR(gradesList, index);
            const isLocalRegional = grade.local_regional_only;
            const isWorldSkate = grade.grade_classification === 'WorldSkate';
            
            return (
              <React.Fragment key={grade.grade_id}>
                {showHR && (
                  <tr>
                    <td colSpan={7} style={{ border: 'none', padding: '16px 0', background: 'white' }}>
                      <div style={{ height: '2px', backgroundColor: '#a8a29e' }}></div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="border px-3 py-2 bg-white font-medium">{grade.name}</td>
                  <td className="border px-1 py-1 bg-white">
                    {renderWarmupCell(
                      grade.grade_id, 
                      'warmup_regionals', 
                      'warmup_regionals_add',
                      isLoops ? '5:00' : '5:00',
                      '2:00',
                      isLoops
                    )}
                  </td>
                  <td className="border px-1 py-1 bg-white">
                    {isLocalRegional ? (
                      <div className="text-center text-muted-foreground text-sm">-</div>
                    ) : renderWarmupCell(
                      grade.grade_id, 
                      'warmup_area_state', 
                      'warmup_area_state_add',
                      isLoops ? '5:00' : '7:00',
                      '2:00',
                      isLoops
                    )}
                  </td>
                  <td className="border px-1 py-1 bg-white">
                    {isLocalRegional ? (
                      <div className="text-center text-muted-foreground text-sm">-</div>
                    ) : renderWarmupCell(
                      grade.grade_id, 
                      'warmup_nationals', 
                      'warmup_nationals_add',
                      isLoops ? '5:00' : '10:00',
                      '2:00',
                      isLoops
                    )}
                  </td>
                  <td className="border px-1 py-1 bg-white">
                    {(isLocalRegional || !isWorldSkate) ? (
                      <div className="text-center text-muted-foreground text-sm">-</div>
                    ) : renderWarmupCell(
                      grade.grade_id, 
                      'warmup_international', 
                      'warmup_international_add',
                      isLoops ? '5:00' : '10:00',
                      '2:00',
                      isLoops
                    )}
                  </td>
                  <td className="border px-1 py-1 bg-white">
                    <Select 
                      value={timing.time_per_figure || defaultTimePerFigure} 
                      onValueChange={(v) => updateGradeTiming(grade.grade_id, 'time_per_figure', v, isLoops)}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                        {FIGURES_TIME_PER_FIGURE_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="border px-3 py-2 bg-white text-center">
                    <Checkbox
                      checked={timing.can_shorten || false}
                      onCheckedChange={(checked) => updateGradeTiming(grade.grade_id, 'can_shorten', checked, isLoops)}
                    />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Figures and Loops</h2>
        <Button onClick={handleAddTimings} className="btn-primary" data-testid="add-timings-figures">
          <Plus className="mr-2 h-4 w-4" />
          Add Timings
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create and edit timing tables for schedules you own. Timing tables for system schedules are view-only.
        </div>
      )}
      
      <Card className="p-6">
        {timingsList.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No timing entries yet for Figures and Loops.</p>
            <p className="text-sm mt-1">Click "Add Timings" to create your first entry.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'figures')}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8"></th>
                    <th className="text-left py-3 px-2 font-semibold">Schedule</th>
                    <th className="text-center py-3 px-2 font-semibold">Grades Configured</th>
                    <th className="text-center py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={timingsList.map(t => t.timing_id)} strategy={verticalListSortingStrategy}>
                    {timingsList.map(timing => (
                      <SortableTimingRow
                        key={timing.timing_id}
                        id={timing.timing_id}
                        timing={timing}
                        canEdit={canEditTiming(timing)}
                        canReorder={isSuperUser}
                        onEdit={handleEditTiming}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) localResetForm(); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTiming ? 'Edit Figures & Loops Timings' : 'Add Figures & Loops Timings'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label>Select Schedule *</Label>
              <Select 
                value={selectedScheduleId} 
                onValueChange={setSelectedScheduleId}
                disabled={!!editingTiming}
              >
                <SelectTrigger data-testid="schedule-select">
                  <SelectValue placeholder="Choose a schedule..." />
                </SelectTrigger>
                <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                  {schedules.map(s => (
                    <SelectItem key={s.schedule_id} value={s.schedule_id}>
                      {s.schedule_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingTiming && (
                <p className="text-xs text-muted-foreground mt-1">Schedule cannot be changed when editing</p>
              )}
            </div>

            {figuresGrades.length > 0 && (
              <div className="overflow-x-auto">
                <h3 className="font-medium mb-2">Figures Grades</h3>
                {renderFiguresTable(figuresGrades, figuresTimings, false)}
              </div>
            )}

            {(figuresGrades.length > 0 || loopsGrades.length > 0) && (
              <div className="space-y-4 py-4">
                <div style={{ height: '2px', backgroundColor: '#a8a29e', marginBottom: '16px' }}></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Add for loop action changes in above grades (applied if less than 8 skaters)</Label>
                    <Input
                      value={loopActionChanges}
                      onChange={(e) => setLoopActionChanges(e.target.value)}
                      onBlur={() => formatTimeOnBlur(loopActionChanges, setLoopActionChanges)}
                      placeholder="6:00"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (max 7:00)</p>
                  </div>
                  
                  <div>
                    <Label>Mid-event warm ups where there are less than 4 skaters (per figure)</Label>
                    <Input
                      value={midEventWarmups}
                      onChange={(e) => setMidEventWarmups(e.target.value)}
                      onBlur={() => formatTimeOnBlur(midEventWarmups, setMidEventWarmups)}
                      placeholder="2:00"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (max 3:00)</p>
                  </div>
                </div>
                <div style={{ height: '2px', backgroundColor: '#a8a29e', marginTop: '16px' }}></div>
              </div>
            )}

            {loopsGrades.length > 0 && (
              <div className="overflow-x-auto">
                <h3 className="font-medium mb-2">Loops Grades</h3>
                {renderFiguresTable(loopsGrades, loopsTimings, true)}
              </div>
            )}

            {selectedScheduleId && figuresGrades.length === 0 && loopsGrades.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No Figures or Loops grades found for this schedule.</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave}
                disabled={!selectedScheduleId || (figuresGrades.length === 0 && loopsGrades.length === 0) || saving}
                className="btn-primary"
              >
                {saving ? 'Saving...' : (editingTiming ? 'Update Timings' : 'Save Timings')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.schedule_name ? `timing for "${itemToDelete.schedule_name}"` : 'this timing'}
        loading={deleting}
      />
    </>
  );
}
